<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'

interface Props {
  open: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
  /** Blocks confirm/cancel/dismiss and shows a spinner on the confirm button. */
  loading?: boolean
  /** Set false when the caller owns closing the dialog (e.g. ConfirmDialogHost). */
  closeOnConfirm?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  confirmText: undefined,
  cancelText: undefined,
  danger: false,
  loading: false,
  closeOnConfirm: true
})

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'cancel'): void
  (e: 'update:open', value: boolean): void
}>()

const { t } = useI18n()

const confirmLabel = computed(() => props.confirmText || t('common.confirm'))
const cancelLabel = computed(() => props.cancelText || t('common.cancel'))

const handleConfirm = () => {
  emit('confirm')
  if (props.closeOnConfirm) {
    emit('update:open', false)
  }
}

const handleCancel = () => {
  if (props.loading) return
  emit('cancel')
  emit('update:open', false)
}

const handleOpenChange = (value: boolean) => {
  if (props.loading) return
  emit('update:open', value)
}
</script>

<template>
  <Modal
    :open="open"
    size="sm"
    :show-close="false"
    @update:open="handleOpenChange"
  >
    <div class="sm:flex sm:items-start">
      <div
        v-if="danger"
        class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 dark:bg-rose-900/30 sm:mx-0 sm:h-10 sm:w-10"
      >
        <ExclamationTriangleIcon
          class="h-6 w-6 text-rose-600 dark:text-rose-400"
          aria-hidden="true"
        />
      </div>
      <div class="mt-3 text-center sm:mt-0 sm:ms-4 sm:text-start">
        <h3 class="text-lg leading-6 font-medium text-body">
          {{ title }}
        </h3>
        <div class="mt-2">
          <p class="text-sm text-label">
            {{ message }}
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        variant="secondary"
        class="me-3"
        :disabled="loading"
        @click="handleCancel"
      >
        {{ cancelLabel }}
      </Button>
      <Button
        :variant="danger ? 'danger' : 'primary'"
        :loading="loading"
        @click="handleConfirm"
      >
        {{ confirmLabel }}
      </Button>
    </template>
  </Modal>
</template>
